import store from "app/store";
import axios from "axios";
import { GetSubscriptionStatusesByBusiness } from "subModule/src/services/subscriptionplans/interface";
import { SubscriptionPlanProfileService } from "subModule/src/core/services";
import AlertMessages from "common/Alert/AlertMessages";
import { setSubscriptionStatuses } from "features/subscription/subscriptionSlice";

/**
 * Feature gate used by routes/UI.
 * Matches sidebar: if the business has no subscription plan, modules stay available.
 * If a plan exists, the feature flag from getplan must be enabled.
 */
export function checkSubscriptions(feature: string) {
  const userInfo = store.getState().user;
  if (userInfo?.type === -1) return true;
  const subscriptions = store.getState().subscriptions;
  if (!subscriptions?.hasPlan) return true;
  const featureEntry = subscriptions.items?.find(
    (s: any) => s != null && Object.prototype.hasOwnProperty.call(s, feature),
  );
  return featureEntry?.[feature] === true;
}

/**
 * Gate a submenu under a parent feature using featureItem flags.
 * Example: INVENTORY_PURCHASING → "PO FLOW" / "DIRECT SR", with optional OR keys (e.g. RFQ).
 * If any `orItems` key is true, access is granted even when `featureItem` is false.
 */
export function checkSubscriptionFeatureItem(
  feature: string,
  featureItem: string,
  orItems: string[] = [],
) {
  const userInfo = store.getState().user;
  if (userInfo?.type === -1) return true;
  const subscriptions = store.getState().subscriptions;
  if (!subscriptions?.hasPlan) return true;

  const featureObj = subscriptions?.items?.find((s: any) => s?.[feature]);
  if (!featureObj) return false;

  const items: Record<string, boolean>[] = Array.isArray(featureObj.featureItem)
    ? featureObj.featureItem
    : [];

  // Legacy plans without featureItem flags: parent feature alone grants access.
  if (!items.length) return true;

  const isItemEnabled = (key: string) =>
    items.some((item) => item?.[key] === true);

  if (orItems.some(isItemEnabled)) return true;
  return isItemEnabled(featureItem);
}

const isCanceled = (err: any) =>
  axios.isCancel?.(err) ||
  err?.name === "CanceledError" ||
  err?.name === "AbortError" ||
  err?.code === "ERR_CANCELED" ||
  err?.message === "Canceled" ||
  err?.message === "New request initiated" ||
  err?.message?.toLowerCase?.()?.includes("cancel");

/** Expected when business has no assigned plan — not a UI error. */
const isNoPlanMessage = (message?: string | null) => {
  const m = (message || "").toLowerCase();
  return (
    m.includes("business plan not found") ||
    m.includes("plan feature not found") ||
    m.includes("plan not found")
  );
};

export function CheckSubscriptionStatuses(
  businessId: number | null | undefined,
  dispatch: any,
  enqueueSnackbar: any,
  onComplete?: () => void,
) {
  SubscriptionPlanProfileService.getSubscriptionStatusesByBusiness(businessId)
    .then((res: GetSubscriptionStatusesByBusiness) => {
      if (res.success) {
        dispatch(
          setSubscriptionStatuses({
            items: res.data != null ? res.data : [],
            hasPlan: res.data != null,
          }),
        );
      } else {
        dispatch(setSubscriptionStatuses({ items: [], hasPlan: false }));
        // Don't toast expected "no plan" responses on every page refresh.
        if (!isNoPlanMessage(res?.message)) {
          enqueueSnackbar(
            <AlertMessages text={res?.message || "Something went wrong"} />,
            { variant: "error" },
          );
        }
      }
    })
    .catch((err: any) => {
      if (isCanceled(err)) return;
      dispatch(setSubscriptionStatuses({ items: [], hasPlan: false }));
      const message =
        err?.message || err?.data?.message || err?.response?.data?.message;
      if (message && !isNoPlanMessage(message)) {
        enqueueSnackbar(<AlertMessages text={message} />, { variant: "error" });
      }
      console.error(err);
    })
    .finally(() => {
      onComplete?.();
    });
}
